import { useState, useEffect } from 'react';
import { addDays } from 'date-fns';
import { toApiDate } from '../lib/dateFormat';
import {
  unterrichtseinheitenApi,
  teacherBlockedTimesApi,
  type UnterrichtseinheitDTO,
  type TeacherBlockedTimeDTO
} from '../lib/unterrichtseinheiten';

export interface EnhancedCourse {
  id: string;
  courseId?: number | null;
  teacherId: string;
  roomId?: number | null;
  name: string;
  fachName?: string | null;
  raumName?: string | null;
  standortName?: string | null;
  standortAdresse?: string | null;
  teilnehmer?: string | null;
  participants?: Array<{ subprofileId: string; wpUserId: number; subIndex: number; name: string }> | null;
  notizen?: string | null;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  startDate: string;
  // Optionaler Hinweis-Text für die Stundenplanansicht. Wird vom Backend-
  // Timetable-Endpoint geliefert ("Start ab 04.06." / "Bis zum 31.07.") und
  // im CourseBlock als Badge angezeigt. Pattern-Events: aus useTeacherTimetable.
  // Unit-Events / Trials haben i.d.R. kein hint.
  hint?: string | null;
  status: string;
  sourceType?: string;
  isPlaceholder?: boolean;
  color: string;
  studentName: string;
  studentEmail: string;
  blockedSource?: string | null;
  isExternalBlocked?: boolean;
  blockedDetails?: {
    title?: string | null;
    location?: string | null;
    description?: string | null;
    attendees?: Array<{ email?: string; displayName?: string; self?: boolean }> | null;
  } | null;
}

/**
 * Custom Hook zum Laden des Lehrer-Stundenplans mit erweiterten Daten
 */
export const useTeacherSchedule = (
  teacherId: string | number | undefined,
  currentWeek: Date,
  standortId?: number | null
) => {
  const [courses, setCourses] = useState<EnhancedCourse[]>([]);
  const [units, setUnits] = useState<UnterrichtseinheitDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mapUnitsToEnhancedCourses = (loadedUnits: UnterrichtseinheitDTO[]): EnhancedCourse[] => {
    return loadedUnits.map(u => {
      const startDate = new Date(u.start_zeit);
      const endDate = new Date(u.end_zeit);
      
      // Farbcodierung nach Status und Typ
      let color = 'bg-blue-100 border-blue-300 text-blue-800';
      if (u.status === 'abgesagt') {
        color = 'bg-red-100 border-red-300 text-red-800';
      } else if (u.status === 'verschoben') {
        color = 'bg-yellow-100 border-yellow-300 text-yellow-800';
      } else if (u.source_type === 'trial') {
        color = 'bg-green-100 border-green-300 text-green-800';
      } else if (u.kurs_farbe) {
        color = u.kurs_farbe;
      }
      
      return {
        id: String(u.id),
        courseId: u.course_id,
        teacherId: String(u.lehrer_id),
        roomId: u.raum_id ?? null,
        name: u.kurs_name || u.fach_name || 'Unterricht',
        fachName: u.fach_name,
        raumName: u.raum_name,
        standortName: u.standort_name,
        standortAdresse: u.standort_adresse,
        teilnehmer: u.teilnehmer_namen,
        participants: (u as any).teilnehmer ?? null,
        notizen: u.notizen ?? null,
        dayOfWeek: ((startDate.getDay() + 6) % 7) + 1,
        startTime: `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`,
        endTime: `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`,
        startDate: toApiDate(startDate),
        status: u.status,
        sourceType: u.source_type,
        isPlaceholder: Number(u.kurs_is_placeholder ?? 0) === 1,
        color,
        studentName: u.teilnehmer_namen || '',
        studentEmail: ''
      };
    });
  };

  const mapBlockedToEnhancedCourses = (blockedTimes: TeacherBlockedTimeDTO[]): EnhancedCourse[] => {
    return blockedTimes
      .filter((entry) => !!entry.date && !!entry.startTime && !!entry.endTime)
      .map((entry) => {
        // Das Backend liefert HH:MM, ältere Stände HH:MM:SS — beides auf HH:MM bringen.
        const startDate = new Date(`${entry.date}T${(entry.startTime || '').slice(0, 5)}:00`);
        const displayTitle = entry.title || 'Geblockt (extern)';
        return {
          id: `blocked-${entry.id}`,
          teacherId: String(entry.teacherId),
          roomId: null,
          name: displayTitle,
          fachName: displayTitle,
          raumName: null,
          standortName: null,
          standortAdresse: null,
          teilnehmer: null,
          participants: null,
          notizen: null,
          dayOfWeek: ((startDate.getDay() + 6) % 7) + 1,
          startTime: (entry.startTime || '').slice(0, 5),
          endTime: (entry.endTime || '').slice(0, 5),
          startDate: toApiDate(startDate),
          status: 'geplant',
          sourceType: 'google_blocked',
          isPlaceholder: false,
          color: 'bg-zinc-100 border-zinc-300 text-zinc-700',
          studentName: '',
          studentEmail: '',
          blockedSource: entry.source || 'google',
          isExternalBlocked: true,
          blockedDetails: {
            title: entry.title || null,
            location: entry.location || null,
            description: entry.description || null,
            attendees: entry.attendees || null,
          },
        };
      });
  };

  useEffect(() => {
    const fetchSchedule = async () => {
      if (!teacherId) {
        setCourses([]);
        setUnits([]);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const start = toApiDate(currentWeek);
        const end = toApiDate(addDays(currentWeek, 6));
        
        const loadedUnits = await unterrichtseinheitenApi.list({ 
          teacherId, 
          standortId: standortId ?? undefined,
          start, 
          end 
        });
        
        setUnits(loadedUnits);
        const blocked = await teacherBlockedTimesApi.list({
          teacherId,
          start,
          end,
        });
        setCourses([...mapUnitsToEnhancedCourses(loadedUnits), ...mapBlockedToEnhancedCourses(blocked)]);
      } catch (err) {
        console.error('Fehler beim Laden des Stundenplans:', err);
        setError(err instanceof Error ? err : new Error('Unbekannter Fehler'));
        setCourses([]);
        setUnits([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSchedule();
  }, [teacherId, currentWeek, standortId]);

  const refetch = async () => {
    if (!teacherId) return;
    
    const start = toApiDate(currentWeek);
    const end = toApiDate(addDays(currentWeek, 6));
    
    try {
      const loadedUnits = await unterrichtseinheitenApi.list({ teacherId, standortId: standortId ?? undefined, start, end });
      const blocked = await teacherBlockedTimesApi.list({ teacherId, start, end });
      setUnits(loadedUnits);
      setCourses([...mapUnitsToEnhancedCourses(loadedUnits), ...mapBlockedToEnhancedCourses(blocked)]);
    } catch (err) {
      console.error('Fehler beim Neuladen:', err);
    }
  };

  return { courses, units, isLoading, error, refetch };
};

